import { useRef, type ReactNode } from 'react';

/**
 * Выбор файла системным окном.
 *
 * Поле `<input type="file">` живёт только здесь — как и любое другое поле ввода
 * ([D-32](docs/DECISIONS.md)): экран, собравший его сам, получил бы системную рамку
 * вместо нашей кнопки и свой набор атрибутов. Правило проверяется `check-rules.mjs`.
 *
 * Поле спрятано, а не заменено: на iOS системное окно открывается **только** щелчком
 * по настоящему `input`, поэтому кнопка нажимает его программно. Значение сбрасывается
 * после каждого выбора — иначе выбор того же файла второй раз подряд не даёт события
 * `change`, и «Добавить» перестаёт работать без единой ошибки.
 */
export function AppFilePicker({
  label,
  accept,
  multiple = false,
  capture,
  variant = 'filled',
  onFiles,
  children,
}: {
  /** Название для доступности: кнопка бывает и без текста. */
  label: string;
  accept?: string;
  multiple?: boolean;
  /**
   * Съёмка вместо выбора: `environment` открывает системную камеру.
   * Живой рамки в видоискателе в вебе нет — деградация записана в WEB_ROADMAP.md.
   */
  capture?: 'environment' | 'user';
  /**
   * Кнопка набирается как обычная кнопка приложения — своих видов у неё нет.
   * `menu` — тот же пункт, что у `AppMenuSheet`: лист источников состоит из них.
   * `row` — строка блока: её ставит `AppSectionRow`, когда нажатие открывает файлы.
   */
  variant?: 'filled' | 'text' | 'menu' | 'row';
  onFiles: (files: File[]) => void;
  /** Содержимое кнопки. Если его нет, показывается [label]. */
  children?: ReactNode;
}) {
  const field = useRef<HTMLInputElement>(null);

  const className =
    variant === 'menu'
      ? 'sheet__item'
      : variant === 'row'
        ? 'section-row'
        : `button ${variant === 'text' ? 'button--text' : ''}`;

  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={label}
        onClick={() => field.current?.click()}
      >
        {children ?? label}
      </button>
      <input
        ref={field}
        className="file-picker__field"
        type="file"
        accept={accept}
        multiple={multiple}
        capture={capture}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = '';
          if (files.length > 0) onFiles(files);
        }}
      />
    </>
  );
}
